import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * TripChat — ephemeral chat between driver and customer while a trip is active.
 *
 * Props:
 *  - bookingId    (required)
 *  - auth         { code, email }  for guest-customer access (omit for session auth)
 *  - viewerHint   string label shown next to "you" (optional, e.g. "You · Customer")
 *  - compact      boolean — render as a compact panel (default false)
 *  - pollMs       number — poll interval (default 5000)
 *
 * Uses /api/bookings/:id/chat (GET + POST).
 */
export default function TripChat({ bookingId, auth, viewerHint = "You", compact = false, pollMs = 5000 }) {
  const [state, setState] = useState({ messages: [], viewer_role: null, status: null, active: false });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const scrollerRef = useRef(null);

  const params = auth ? { code: auth.code, email: auth.email } : {};

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/bookings/${bookingId}/chat`, { params });
      setState(r.data);
      setError(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not load chat";
      setError(msg);
    }
  }, [bookingId, JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, pollMs);
    return () => clearInterval(id);
  }, [refresh, pollMs]);

  // Scroll to bottom on new message
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [state.messages.length]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const payload = auth ? { text, code: auth.code, email: auth.email } : { text };
      const r = await api.post(`/bookings/${bookingId}/chat`, payload);
      setState(s => ({ ...s, messages: [...s.messages, r.data] }));
      setText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send message");
    } finally {
      setBusy(false);
    }
  };

  const viewerRole = state.viewer_role;
  const hostHeight = compact ? "h-48" : "h-72";

  if (error) {
    return (
      <div data-testid="trip-chat" className="border border-white/10 bg-[#0a0a0a] p-4">
        <div className="overline mb-1">Trip chat</div>
        <div className="text-xs text-[#A1A1AA]">{error}</div>
      </div>
    );
  }

  return (
    <div data-testid="trip-chat" className="border border-white/10 bg-[#0a0a0a] flex flex-col">
      <div className="px-4 py-3 border-b border-white/10 flex justify-between items-center">
        <div>
          <div className="overline">Trip chat</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] mt-0.5">
            {state.active ? `Live · ${state.status}` : "Closed (trip not active)"}
          </div>
        </div>
        {viewerRole && (
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] border border-[#D4AF37]/30 px-2 py-0.5">{viewerHint}</span>
        )}
      </div>

      <div ref={scrollerRef} className={`${hostHeight} overflow-y-auto p-3 space-y-2`} data-testid="trip-chat-scroller">
        {state.messages.length === 0 && (
          <div className="text-center text-[11px] text-[#71717A] py-8">
            No messages yet. Say hi to coordinate pickup.
          </div>
        )}
        {state.messages.map(m => {
          const mine = m.sender_role === viewerRole;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                data-testid={`chat-msg-${m.message_id}`}
                className={`max-w-[78%] px-3 py-2 text-sm ${
                  mine
                    ? "bg-[#D4AF37] text-[#0a0a0a] rounded-l-md rounded-tr-md"
                    : "bg-[#141414] text-[#F5F2EB] border border-white/10 rounded-r-md rounded-tl-md"
                }`}
              >
                <div className={`text-[9px] uppercase tracking-[0.18em] mb-0.5 ${mine ? "text-black/60" : "text-[#71717A]"}`}>
                  {m.sender_name} · {new Date(m.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                </div>
                <div className="whitespace-pre-wrap break-words leading-snug">{m.text}</div>
              </div>
            </div>
          );
        })}
      </div>

      {state.active ? (
        <div className="border-t border-white/10 p-2 flex gap-2">
          <input
            data-testid="trip-chat-input"
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Type a message…"
            maxLength={1000}
            className="flex-1 bg-transparent border border-white/10 px-3 py-2 text-sm text-[#F5F2EB] placeholder:text-[#71717A] focus:outline-none focus:border-[#D4AF37]/60"
          />
          <button
            data-testid="trip-chat-send"
            onClick={send}
            disabled={busy || !text.trim()}
            className="btn-gold !py-2 !px-4 text-[11px]"
          >{busy ? "…" : "Send"}</button>
        </div>
      ) : (
        <div className="border-t border-white/10 p-3 text-[11px] text-[#71717A]">
          Messaging is available while your trip is assigned, en route, or in progress.
        </div>
      )}
    </div>
  );
}
